import React, { useEffect, useState } from "react";
import { Helmet } from "react-helmet";
import AOS from "aos";
import "aos/dist/aos.css";
import { useLocation } from "react-router-dom";
import { Mail, MapPin, Phone, Globe, Send, Clock, Users, CheckCircle2 } from "lucide-react";
import { toast } from "react-toastify";
import api from "../api";

const Divider = () => (
  <div className="w-full h-1 bg-gradient-to-r from-transparent via-[#FF4D00] to-transparent max-w-6xl mx-auto my-16 opacity-60" />
);

const ContactPage = () => {
  const location = useLocation();

  const [formData, setFormData] = useState({
    name: "",
    email: "",
    organization: "",
    department: "",
    inquiryType: "",
    message: "",
  });

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitSuccess, setSubmitSuccess] = useState(false);

  useEffect(() => {
    if (location.state) {
      setFormData((prev) => ({
        ...prev,
        name: location.state.name || "",
        email: location.state.email || "",
        inquiryType: location.state.inquiryType || "",
      }));
    }
  }, [location.state]);

  useEffect(() => {
    AOS.init({ duration: 800, once: true });
  }, []);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);

    try {
      const response = await api.post("users/contact-form/", formData);

      if (response.status >= 200 && response.status < 300) {
        setSubmitSuccess(true);
        toast.success("✅ Your message has been sent successfully!", { autoClose: 5000 });
        setFormData({
          name: "",
          email: "",
          organization: "",
          department: "",
          inquiryType: "",
          message: "",
        });

        setTimeout(() => setSubmitSuccess(false), 5000);
      }
    } catch (error) {
      console.error("Error submitting form:", error);
      const errorMessage =
        error.response?.data?.error || "An unexpected error occurred. Please try again.";
      toast.error(`❌ Error: ${errorMessage}`, { autoClose: 5000 });
    } finally {
      setIsSubmitting(false);
    }
  };

  const contactInfo = [
    {
      icon: <MapPin className="w-6 h-6" />,
      title: "Address",
      details: [
        "C105 THE SAILS",
        "14 TIMEBALL BOULEVARD",
        "POINT WATERFRONT, DURBAN, 4000",
        "South Africa",
      ],
    },
    {
      icon: <Phone className="w-6 h-6" />,
      title: "Phone",
      details: [
        "+27 (0)73 190 3967",
        "+27 (0)84 310 1221",
      ],
    },
    {
      icon: <Mail className="w-6 h-6" />,
      title: "Email",
      details: [
        "info@izingcweti.co.za",
      ],
      link: "mailto:info@izingcweti.co.za",
    },
    {
      icon: <Globe className="w-6 h-6" />,
      title: "Website",
      details: [
        "www.izingcweti.co.za",
        "Izingcweti ICT Solutions",
      ],
      link: "https://www.izingcweti.co.za",
    },
  ];

  const responseMetrics = [
    { icon: <Clock className="w-10 h-10 text-[#FF4D00]" />, label: "Response Time", value: "Within 48 hours" },
    { icon: <Users className="w-10 h-10 text-[#FF4D00]" />, label: "Support Team", value: "Always Available" },
    { icon: <CheckCircle2 className="w-10 h-10 text-[#FF4D00]" />, label: "Satisfaction", value: "98% Rating" },
  ];

  return (
    <div className="min-h-screen bg-white text-gray-800 font-sans">
      <Helmet>
        <title>Contact Us | Governance and Business Continuity Assessment Tool</title>
        <meta
          name="description"
          content="Contact us for support, access requests, demos, or questions about the Governance and Business Continuity Assessment Tool."
        />
      </Helmet>

      <section className="relative py-20 px-6 bg-gradient-to-br from-gray-50 to-gray-100 overflow-hidden">
        <div className="absolute inset-0 opacity-10 pointer-events-none">
          <div className="absolute top-0 right-0 w-96 h-96 bg-[#FF4D00] rounded-full blur-3xl"></div>
        </div>

        <div className="max-w-5xl mx-auto text-center relative z-10" data-aos="fade-down">
          <h1 className="text-5xl sm:text-6xl font-bold mb-6">
            Get in <span className="text-transparent bg-clip-text bg-gradient-to-r from-[#FF4D00] to-orange-600">Touch</span>
          </h1>
          <p className="text-xl text-gray-600 max-w-3xl mx-auto leading-relaxed">
            Have questions, need support, want to request access, or interested in a demo? Our team is here to help.
          </p>
        </div>
      </section>

      <Divider />

      <section className="py-20 px-6">
        <div className="max-w-6xl mx-auto">
          <h2 className="text-4xl font-bold text-center mb-16" data-aos="fade-up">
            Contact <span className="text-[#FF4D00]">Information</span>
          </h2>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {contactInfo.map((info, idx) => (
              <div
                key={idx}
                className="bg-white p-8 rounded-2xl shadow-lg hover:shadow-2xl transition duration-300 border border-gray-200 group"
                data-aos="fade-up"
                data-aos-delay={idx * 100}
              >
                <div className="mb-6 p-4 bg-[#FF4D00]/10 rounded-xl w-fit group-hover:bg-[#FF4D00]/20 transition">
                  <div className="text-[#FF4D00]">{info.icon}</div>
                </div>

                <h3 className="text-lg font-bold text-gray-900 mb-4">
                  {info.title}
                </h3>

                <div className="space-y-2">
                  {info.details.map((detail, didx) => (
                    <p key={didx} className="text-gray-700 text-sm leading-relaxed">
                      {info.link ? (
                        <a
                          href={info.link}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="text-[#FF4D00] hover:underline font-medium"
                        >
                          {detail}
                        </a>
                      ) : (
                        detail
                      )}
                    </p>
                  ))}
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      <Divider />

      <section className="py-20 px-6 bg-gray-50">
        <div className="max-w-6xl mx-auto">
          <div className="grid md:grid-cols-3 gap-8">
            {responseMetrics.map((metric, idx) => (
              <div
                key={idx}
                className="text-center p-8 rounded-xl hover:bg-white hover:shadow-lg transition duration-300"
                data-aos="zoom-in"
                data-aos-delay={idx * 100}
              >
                <div className="flex justify-center mb-4">
                  {metric.icon}
                </div>
                <p className="text-gray-600 text-sm mb-2 font-medium">{metric.label}</p>
                <p className="text-2xl font-bold text-gray-900">{metric.value}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <Divider />

      <section className="py-20 px-6">
        <div className="max-w-4xl mx-auto">
          {submitSuccess && (
            <div
              className="mb-8 p-6 bg-green-50 border border-green-200 rounded-xl flex items-start gap-4"
              data-aos="fade-down"
            >
              <CheckCircle2 className="text-green-600 flex-shrink-0 mt-0.5" size={24} />
              <div>
                <h3 className="font-bold text-green-900 mb-1">Message Sent Successfully!</h3>
                <p className="text-green-800">
                  Thank you for contacting us. Our team will review your message and respond as soon as possible.
                </p>
              </div>
            </div>
          )}

          <div className="bg-white rounded-2xl shadow-xl overflow-hidden border border-gray-200">
            <div className="bg-gradient-to-r from-[#FF4D00] to-orange-600 text-white p-8 sm:p-12">
              <h2 className="text-3xl sm:text-4xl font-bold mb-3">Send Us a Message</h2>
              <p className="text-orange-100 text-lg">
                Fill out the form below and our team will get back to you as soon as possible.
              </p>
            </div>

            <div className="p-8 sm:p-12">
              <form onSubmit={handleSubmit} className="space-y-6">
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
                  <div>
                    <label className="block text-sm font-semibold text-gray-900 mb-2">
                      Full Name <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="text"
                      name="name"
                      value={formData.name}
                      onChange={handleChange}
                      placeholder="Sifiso Sibisi"
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#FF4D00] transition"
                      required
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-semibold text-gray-900 mb-2">
                      Email <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="email"
                      name="email"
                      value={formData.email}
                      onChange={handleChange}
                      placeholder="sifiso@izingcweti.co.za"
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#FF4D00] transition"
                      required
                    />
                  </div>
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
                  <div>
                    <label className="block text-sm font-semibold text-gray-900 mb-2">
                      Organization <span className="text-gray-500 text-xs">(Optional)</span>
                    </label>
                    <input
                      type="text"
                      name="organization"
                      value={formData.organization}
                      onChange={handleChange}
                      placeholder="Your Company"
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#FF4D00] transition"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-semibold text-gray-900 mb-2">
                      Department <span className="text-gray-500 text-xs">(Optional)</span>
                    </label>
                    <input
                      type="text"
                      name="department"
                      value={formData.department}
                      onChange={handleChange}
                      placeholder="IT Governance"
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#FF4D00] transition"
                    />
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-semibold text-gray-900 mb-2">
                    Inquiry Type <span className="text-red-500">*</span>
                  </label>
                  <select
                    name="inquiryType"
                    value={formData.inquiryType}
                    onChange={handleChange}
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#FF4D00] transition"
                    required
                  >
                    <option value="">Select an inquiry type</option>
                    <option value="Support">Support</option>
                    <option value="Request Access">Request Access</option>
                    <option value="Demo">Request Demo</option>
                    <option value="Feedback">Feedback</option>
                    <option value="Partnership">Partnership</option>
                    <option value="Other">Other</option>
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-semibold text-gray-900 mb-2">
                    Message <span className="text-red-500">*</span>
                  </label>
                  <textarea
                    name="message"
                    rows="6"
                    value={formData.message}
                    onChange={handleChange}
                    placeholder="Tell us how we can help you..."
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#FF4D00] transition resize-none"
                    required
                  ></textarea>
                </div>

                <button
                  type="submit"
                  disabled={isSubmitting}
                  className="w-full bg-gradient-to-r from-[#FF4D00] to-orange-600 text-white py-4 rounded-lg font-semibold hover:shadow-lg transform hover:scale-105 transition duration-300 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
                >
                  {isSubmitting ? (
                    <>
                      <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white"></div>
                      Sending...
                    </>
                  ) : (
                    <>
                      <Send size={20} />
                      Send Message
                    </>
                  )}
                </button>
              </form>

              <div className="mt-8 pt-8 border-t border-gray-200">
                <p className="text-gray-600 text-sm text-center">
                  <span className="font-semibold">Questions before submitting?</span><br />
                  Call us at{" "}
                  <a href="tel:+27731903967" className="text-[#FF4D00] hover:underline font-medium">
                    +27 (0)73 190 3967
                  </a>{" "}
                  or email{" "}
                  <a href="mailto:info@izingcweti.co.za" className="text-[#FF4D00] hover:underline font-medium">
                    info@izingcweti.co.za
                  </a>
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      <section className="py-20 px-6 bg-gray-50">
        <div className="max-w-4xl mx-auto">
          <h2 className="text-4xl font-bold text-center mb-16" data-aos="fade-up">
            Frequently Asked <span className="text-[#FF4D00]">Questions</span>
          </h2>

          <div className="space-y-6">
            {[
              {
                q: "What's the typical response time?",
                a: "We aim to respond to all inquiries within 24 hours during business days.",
              },
              {
                q: "Do you offer free demos?",
                a: "Yes. We offer demos based on your organization's needs. Contact us to arrange one.",
              },
              {
                q: "What support options are available?",
                a: "We provide email support, phone support, and additional support options for enterprise customers.",
              },
              {
                q: "What if I answered a question incorrectly or uploaded the wrong document?",
                a: "No problem. Please submit a support request and specify which question was answered incorrectly or which document needs to be replaced. Include the correct information where possible. Our admin team will review your request and update your assessment accordingly.",
              },
              {
                q: "Are generated reports final?",
                a: "At this stage, exported reports should be treated as interim reports for review and follow-up, rather than finalized reporting outputs.",
              },
            ].map((faq, idx) => (
              <div
                key={idx}
                className="bg-white p-6 rounded-xl border border-gray-200 hover:shadow-lg transition duration-300"
                data-aos="fade-up"
                data-aos-delay={idx * 80}
              >
                <h3 className="font-bold text-gray-900 mb-2 flex items-start gap-3">
                  <span className="text-[#FF4D00] font-bold flex-shrink-0">Q:</span>
                  {faq.q}
                </h3>
                <p className="text-gray-700 ml-6">
                  <span className="text-[#FF4D00] font-bold">A: </span>
                  {faq.a}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <Divider />

      <section className="py-16 px-6 bg-gradient-to-r from-[#FF4D00]/5 to-orange-600/5">
        <div className="max-w-6xl mx-auto text-center">
          <p className="text-gray-600 font-medium mb-8">Trusted by Organizations in South Africa</p>
          <div className="flex flex-wrap justify-center gap-8 text-gray-600 text-sm font-medium">
            <div className="flex items-center gap-2">
              <CheckCircle2 className="text-[#FF4D00]" size={20} />
              ISO Certified
            </div>
            <div className="flex items-center gap-2">
              <CheckCircle2 className="text-[#FF4D00]" size={20} />
              Enterprise Security
            </div>
            <div className="flex items-center gap-2">
              <CheckCircle2 className="text-[#FF4D00]" size={20} />
              Ongoing Support
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default ContactPage;